import React from 'react';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend
} from 'recharts';

const styles = {
    container: {
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '24px',
        color: 'var(--text-main)'
    },
    grid3: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: '20px'
    },
    card: {
        background: 'var(--bg-box)',
        border: '1px solid var(--border-color)',
        borderRadius: '16px',
        padding: '20px',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'space-between',
        minHeight: '180px',
        position: 'relative' as const,
        overflow: 'hidden' as const,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    },
    activeCard: {
        borderColor: '#7c3aed',
        boxShadow: '0 0 20px rgba(124, 58, 237, 0.15)',
        background: 'linear-gradient(135deg, var(--bg-box) 0%, rgba(124, 58, 237, 0.05) 100%)'
    },
    perfValue: {
        fontSize: '28px',
        fontWeight: 800,
        letterSpacing: '-0.02em',
        color: '#7c3aed',
        marginTop: '12px'
    },
    perfLabel: {
        fontSize: '12px',
        color: 'var(--text-muted)',
        marginTop: '4px'
    },
    chartContainer: {
        background: 'var(--bg-box)',
        border: '1px solid var(--border-color)',
        borderRadius: '16px',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '16px'
    },
    sectionTitle: {
        fontSize: '18px',
        fontWeight: 700,
        color: 'var(--text-heading)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    tableContainer: {
        background: 'var(--bg-box)',
        border: '1px solid var(--border-color)',
        borderRadius: '16px',
        overflow: 'hidden'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse' as const,
        textAlign: 'left' as const
    },
    th: {
        padding: '14px 20px',
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'uppercase' as const,
        color: 'var(--text-muted)',
        borderBottom: '1px solid var(--border-color)',
        background: 'rgba(0, 0, 0, 0.05)'
    },
    td: {
        padding: '16px 20px',
        fontSize: '14px',
        borderBottom: '1px solid var(--border-color)',
        color: 'var(--text-main)'
    },
    paywallContainer: {
        position: 'relative' as const,
        width: '100%'
    },
    timeline: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '24px',
        padding: '8px 4px',
        position: 'relative' as const
    },
    timelineItem: {
        display: 'flex',
        gap: '16px',
        position: 'relative' as const
    },
    timelineLine: {
        position: 'absolute' as const,
        left: '20px',
        top: '40px',
        bottom: '-24px',
        width: '2px',
        backgroundColor: 'var(--border-color)'
    },
    timelineIcon: {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        zIndex: 2,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    },
    timelineContent: {
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid var(--border-color)',
        borderRadius: '12px',
        padding: '16px',
        flexGrow: 1
    }
};

const CustomChartTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;
    const p1 = payload[0];
    const p2 = payload[1];
    
    return (
        <div style={{
            background: 'var(--bg-box)',
            border: '1px solid var(--border-color)',
            borderRadius: 12,
            padding: '12px 16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            fontSize: 13,
            minWidth: 200,
            backdropFilter: 'blur(10px)'
        }}>
            <div style={{ fontWeight: 700, marginBottom: 8, color: 'var(--text-heading)' }}>
                {label ? new Date(label).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : '—'}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 6 }}>
                <span style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#7c3aed' }}></span>
                    Stratégie IA
                </span>
                <span style={{ fontWeight: 800, color: '#a78bfa' }}>{p1?.value !== null && p1?.value !== undefined ? `${p1.value >= 0 ? '+' : ''}${p1.value.toFixed(1)}` : '0.0'}%</span>
            </div>
            {p2 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                    <span style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#64748b' }}></span>
                        BRVM Composite
                    </span>
                    <span style={{ fontWeight: 700, color: '#94a3b8' }}>{p2?.value !== null && p2?.value !== undefined ? `${p2.value >= 0 ? '+' : ''}${p2.value.toFixed(1)}` : '0.0'}%</span>
                </div>
            )}
        </div>
    );
};

interface DesktopAiWatchlistProps {
    strategies: any[];
    selectedId: number | null;
    setSelectedId: (id: number) => void;
    details: any;
    history: any[];
    loading: boolean;
    detailsLoading: boolean;
    isPro: boolean;
    handleUpgrade: () => void;
    formatDate: (dateString: string) => string;
    getRiskLabelStyle: (profile: string) => React.CSSProperties;
}

const DesktopAiWatchlist: React.FC<DesktopAiWatchlistProps> = ({
    strategies,
    selectedId,
    setSelectedId,
    details,
    history,
    loading,
    detailsLoading,
    isPro,
    handleUpgrade,
    formatDate,
    getRiskLabelStyle
}) => {
    return (
        <div style={styles.container}>
            {/* Intro section */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <h2 style={{ fontSize: '24px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-heading)' }}>
                    Watchlists IA : des sélections thématiques de la BRVM
                </h2>
                <p style={{ color: 'var(--text-muted)', fontSize: '15px', lineHeight: '1.6', maxWidth: '800px' }}>
                    Chaque watchlist regroupe des valeurs de la BRVM autour d'un thème, sélectionnées par nos algorithmes et révisées périodiquement.
                    Sa performance est affichée face à l'indice, qu'elle lui soit supérieure ou inférieure ; les performances passées ne préjugent pas des performances futures.
                </p>
            </div>

            {/* Strategies Selector Grid */}
            <div style={styles.grid3}>
                {loading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} style={{ ...styles.card, height: 180, background: 'var(--bg-box)', opacity: 0.5 }}>
                            <div style={{ height: 20, width: '40%', backgroundColor: 'var(--border-color)', borderRadius: 4 }} />
                            <div style={{ height: 40, width: '80%', backgroundColor: 'var(--border-color)', borderRadius: 4, marginTop: 12 }} />
                            <div style={{ height: 30, width: '50%', backgroundColor: 'var(--border-color)', borderRadius: 4, marginTop: 20 }} />
                        </div>
                    ))
                ) : (
                    strategies.map(s => {
                        const isSelected = selectedId === s.id;
                        const riskText = s.risk_profile === 'HIGH' ? 'Risque Élevé' : s.risk_profile === 'MEDIUM' ? 'Risque Moyen' : 'Risque Modéré';
                        
                        // Badge "Révisé ce mois" : last_revision_date dans le mois courant
                        const now = new Date();
                        const lastRev = s.last_revision_date ? new Date(s.last_revision_date) : null;
                        const revisedThisMonth = lastRev &&
                            lastRev.getMonth() === now.getMonth() &&
                            lastRev.getFullYear() === now.getFullYear();

                        const nextRev = s.next_revision_date
                            ? new Date(s.next_revision_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
                            : null;

                        return (
                            <div
                                key={s.id}
                                style={{
                                    ...styles.card,
                                    ...(isSelected ? styles.activeCard : {})
                                }}
                                onClick={() => setSelectedId(s.id)}
                            >
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                                        <span style={getRiskLabelStyle(s.risk_profile)}>{riskText}</span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            {revisedThisMonth && (
                                                <span style={{
                                                    padding: '3px 8px',
                                                    borderRadius: 12,
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    background: 'rgba(16, 185, 129, 0.15)',
                                                    color: '#10b981',
                                                    letterSpacing: '0.04em'
                                                }}>✦ RÉVISÉ CE MOIS</span>
                                            )}
                                            {s.strategy_type === 'GROWTH' && <i className="material-icons" style={{ color: '#ef4444', fontSize: 20 }}>trending_up</i>}
                                            {s.strategy_type === 'YIELD' && <i className="material-icons" style={{ color: '#10b981', fontSize: 20 }}>monetization_on</i>}
                                            {s.strategy_type === 'DEFENSIVE' && <i className="material-icons" style={{ color: '#3b82f6', fontSize: 20 }}>anchor</i>}
                                            {(s.strategy_type === 'VALUE' || s.strategy_type === 'VALUE_MULTIPLES') && <i className="material-icons" style={{ color: '#a855f7', fontSize: 20 }}>diamond</i>}
                                            {s.strategy_type === 'CONTRARIAN' && <i className="material-icons" style={{ color: '#f59e0b', fontSize: 20 }}>track_changes</i>}
                                            {s.strategy_type === 'SECTORIAL' && <i className="material-icons" style={{ color: '#14b8a6', fontSize: 20 }}>public</i>}
                                        </div>
                                    </div>
                                    <h3 style={{ fontSize: '18px', fontWeight: 800, marginTop: '12px', color: 'var(--text-heading)' }}>
                                        {s.name}
                                    </h3>
                                    <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '8px', lineHeight: '1.4' }}>
                                        {s.description}
                                    </p>
                                </div>
                                <div>
                                    <div style={styles.perfValue}>
                                        {(s.performance_cumulated ?? 0) >= 0 ? '+' : ''}{s.performance_cumulated?.toFixed(1) ?? '0.0'}%
                                    </div>
                                    <div style={styles.perfLabel}>
                                        Performance cumulée vs {s.benchmark_performance_cumulated >= 0 ? '+' : ''}{s.benchmark_performance_cumulated?.toFixed(1) ?? '0.0'}% BRVM
                                    </div>
                                    {s.strategy_type === 'YIELD' && (
                                        <div style={{ marginTop: 6, fontSize: 11, color: '#10b981', display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <i className="material-icons" style={{ fontSize: 13 }}>payments</i>
                                            Hors dividendes perçus (Total Return supérieur)
                                        </div>
                                    )}
                                    {/* Échéance passée sans révision : ne jamais présenter
                                        une date échue comme « prochaine révision ». */}
                                    {s.revision_en_retard ? (
                                        <div
                                            style={{ marginTop: 8, fontSize: 11, color: '#d97706', display: 'flex', alignItems: 'center', gap: 4 }}
                                            title={nextRev ? `Révision prévue le ${nextRev}` : undefined}
                                        >
                                            <i className="material-icons" style={{ fontSize: 13 }}>update</i>
                                            Révision reportée · sélection maintenue
                                        </div>
                                    ) : nextRev && (
                                        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <i className="material-icons" style={{ fontSize: 13 }}>schedule</i>
                                            Prochaine révision : {nextRev}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Main performance chart */}
            {detailsLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 180, color: 'var(--text-muted)', gap: 10 }}>
                    <i className="material-icons spin" style={{ fontSize: 24 }}>hourglass_empty</i>
                    <span style={{ fontSize: 14 }}>Chargement de la stratégie...</span>
                </div>
            ) : details && (
                <div style={styles.chartContainer}>
                    <div style={styles.sectionTitle}>
                        <i className="material-icons" style={{ color: '#7c3aed' }}>show_chart</i>
                        Performance Historique vs BRVM Composite
                    </div>
                    
                    <div style={{ width: '100%', height: '320px', marginTop: '16px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={details.performance_history || []}>
                                <defs>
                                    <linearGradient id="ai-gradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#7c3aed" stopOpacity={0.01} />
                                    </linearGradient>
                                    <linearGradient id="bench-gradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#64748b" stopOpacity={0.15} />
                                        <stop offset="95%" stopColor="#64748b" stopOpacity={0.01} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" opacity={0.3} />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={d => new Date(d).toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' })}
                                    tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    tickFormatter={v => `${v >= 0 ? '+' : ''}${v}%`}
                                    tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <Tooltip content={<CustomChartTooltip />} />
                                <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 10 }} />
                                <Area
                                    name="Stratégie IA"
                                    type="monotone"
                                    dataKey="performance_pct"
                                    stroke="#7c3aed"
                                    strokeWidth={3}
                                    fill="url(#ai-gradient)"
                                />
                                <Area
                                    name="BRVM Composite"
                                    type="monotone"
                                    dataKey="benchmark_performance_pct"
                                    stroke="#64748b"
                                    strokeWidth={2}
                                    fill="url(#bench-gradient)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* Selections Section with Freemium Blur */}
            {!detailsLoading && details && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                        <div style={styles.sectionTitle}>
                            <i className="material-icons" style={{ color: '#7c3aed' }}>playlist_add_check</i>
                            Sélection d'Actions (Mois Courant)
                        </div>
                        {details.strategy?.last_revision_date && (
                            <span style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <i className="material-icons" style={{ fontSize: 14, color: '#10b981' }}>verified</i>
                                Dernière révision IA : <strong style={{ color: 'var(--text-main)', marginLeft: 3 }}>{formatDate(details.strategy.last_revision_date)}</strong>
                                {details.strategy?.revision_en_retard ? (
                                    <span style={{ marginLeft: 8, color: '#d97706' }}>
                                        · Révision du {formatDate(details.strategy.next_revision_date)} reportée, sélection maintenue
                                    </span>
                                ) : details.strategy?.next_revision_date && (
                                    <span style={{ marginLeft: 8 }}>
                                        · Prochaine : <strong style={{ color: 'var(--text-main)' }}>{formatDate(details.strategy.next_revision_date)}</strong>
                                    </span>
                                )}
                            </span>
                        )}
                    </div>

                    <div style={styles.paywallContainer}>
                        <div style={styles.tableContainer}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Symbole</th>
                                        <th style={styles.th}>Date d'Ajout</th>
                                        <th style={styles.th}>Prix d'Entrée</th>
                                        <th style={styles.th}>+/- val.</th>
                                        <th style={styles.th}>Catégorie</th>
                                        <th style={styles.th}>Raisonnement IA</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {details.stocks?.map((s: any) => {
                                        const gainPct: number | null = s.gain_pct ?? null;
                                        const gainColor = gainPct === null ? 'var(--text-muted)'
                                            : gainPct >= 0 ? '#10b981' : '#ef4444';
                                        return (
                                            <tr key={s.id}>
                                                <td style={{ ...styles.td, fontWeight: 700, color: isPro ? '#7c3aed' : 'var(--text-muted)' }}>
                                                    {s.stock_symbol}
                                                </td>
                                                <td style={styles.td}>{formatDate(s.added_date)}</td>
                                                <td style={styles.td}>{isPro ? `${s.added_price?.toLocaleString() ?? '0'} XOF` : '•••• XOF'}</td>
                                                <td style={{ ...styles.td, fontWeight: 700, color: gainColor }}>
                                                    {isPro
                                                        ? gainPct !== null
                                                            ? `${gainPct >= 0 ? '+' : ''}${gainPct.toFixed(2)}%`
                                                            : '—'
                                                        : '••%'
                                                    }
                                                </td>
                                                <td style={styles.td}>
                                                    <span style={{
                                                        padding: '4px 8px',
                                                        borderRadius: '6px',
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        background: 'rgba(124, 58, 237, 0.1)',
                                                        color: '#a78bfa'
                                                    }}>
                                                        {s.reason_category}
                                                    </span>
                                                </td>
                                                <td style={{ ...styles.td, color: isPro ? 'var(--text-main)' : 'var(--text-muted)', fontStyle: isPro ? 'normal' : 'italic' }}>
                                                    {s.reasoning}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Paywall Banner Overlay */}
                        {!isPro && (
                            <div className="paywall-overlay" style={{ borderRadius: '0 0 16px 16px' }}>
                                <div className="paywall-icon-circle" style={{ width: '60px', height: '60px', marginBottom: '16px' }}>
                                    <i className="material-icons" style={{ fontSize: '32px', color: '#7c3aed' }}>lock</i>
                                </div>
                                <h3 className="paywall-title" style={{ fontSize: '20px', marginBottom: '8px' }}>
                                    Accès Réservé aux Membres Kanea Pro
                                </h3>
                                <p className="paywall-desc" style={{ maxWidth: '480px', marginBottom: '20px' }}>
                                    Consultez les valeurs sélectionnées ce mois-ci, les seuils d'entrée identifiés et le raisonnement quantitatif associé à chaque position.
                                </p>
                                <button onClick={handleUpgrade} className="paywall-btn">
                                    Débloquer maintenant
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* History Timeline */}
            {details && history.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
                    <div style={styles.sectionTitle}>
                        <i className="material-icons" style={{ color: '#7c3aed' }}>history</i>
                        Historique des Mouvements de Composition
                    </div>

                    <div style={styles.timeline}>
                        {(isPro ? history : history.slice(0, 3)).map((m: any, idx: number) => {
                            const isAdded = m.status === 'ACTIVE';
                            return (
                                <div key={m.id} style={styles.timelineItem}>
                                    {idx < history.length - 1 && <div style={styles.timelineLine}></div>}
                                    
                                    <div style={{
                                        ...styles.timelineIcon,
                                        backgroundColor: isAdded ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                        color: isAdded ? '#10b981' : '#ef4444'
                                    }}>
                                        <i className="material-icons" style={{ fontSize: '20px' }}>
                                            {isAdded ? 'add_circle' : 'remove_circle'}
                                        </i>
                                    </div>

                                    <div style={styles.timelineContent}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                            <span style={{ fontSize: '15px', fontWeight: 800, color: 'var(--text-heading)' }}>
                                                {isAdded ? '🟢 AJOUTÉ :' : '🔴 RETIRÉ :'} {isPro ? m.stock_symbol : 'LOCKED'}
                                            </span>
                                            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                                                {isAdded ? formatDate(m.added_date) : formatDate(m.removed_date || m.added_date)}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', gap: '12px', marginTop: '8px', fontSize: '13px', color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                                            <span>Prix d'entrée : {isPro ? `${m.added_price?.toLocaleString() ?? '0'} XOF` : '•••• XOF'}</span>
                                            {!isAdded && m.removed_price && (
                                                <span>Prix de sortie : {isPro ? `${m.removed_price?.toLocaleString() ?? '0'} XOF` : '•••• XOF'}</span>
                                            )}
                                            {m.gain_pct !== null && m.gain_pct !== undefined && isPro && (
                                                <span style={{
                                                    fontWeight: 700,
                                                    color: m.gain_pct >= 0 ? '#10b981' : '#ef4444'
                                                }}>
                                                    {m.gain_pct >= 0 ? '+' : ''}{m.gain_pct.toFixed(2)}%
                                                </span>
                                            )}
                                        </div>
                                        <p style={{ marginTop: '12px', fontSize: '14px', lineHeight: '1.5', color: 'var(--text-main)' }}>
                                            {m.reasoning}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}

                        {/* Teaser verrouillé pour non-pro */}
                        {!isPro && history.length > 3 && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '16px 20px',
                                borderRadius: '12px',
                                border: '1px dashed rgba(124, 58, 237, 0.4)',
                                background: 'rgba(124, 58, 237, 0.06)',
                                marginTop: '4px'
                            }}>
                                <i className="material-icons" style={{ color: '#7c3aed', fontSize: '20px' }}>lock</i>
                                <div style={{ flex: 1 }}>
                                    <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-heading)' }}>
                                        {history.length - 3} autres mouvements verrouillés
                                    </span>
                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                                        Accédez à l'historique complet des décisions IA avec Kanea Pro.
                                    </div>
                                </div>
                                <button onClick={handleUpgrade} style={{
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    background: '#7c3aed',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '13px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap'
                                }}>
                                    Pro ⚡
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DesktopAiWatchlist;
